import {
  cartFromMutationResponse,
  CartApi,
  executeOperations,
  verifyOneTime,
  verifyPlan,
} from "./subscription-cart-api.js?v=20260910-batch-1";
import { deriveCartState, ineligibleTitlesForPlan } from "./subscription-cart-state.js?v=20260901-performance";
import {
  buildActivationTransaction,
  buildDeactivationPlan,
  buildFrequencyPlan,
  activationContentsMatch,
  cartMatchesSnapshot,
  deactivationMatchesSnapshot,
  recoverDeactivation,
  rollbackActivation,
} from "./subscription-cart-operations.js?v=20260908-bundle-recovery";

function restoredCartMessage(error) {
  const description = error.message.endsWith(".") ? error.message : `${error.message}.`;
  return `${description} Your original cart has been restored.`;
}

function activationMatchesSnapshot(cart, snapshot) {
  return snapshot?.cart ? cartMatchesSnapshot(cart, snapshot) : verifyOneTime(cart);
}

function deactivationMessage(reason) {
  const messages = {
    "below-minimum": "Your cart was switched to one-time purchase because subscriptions require 12 bottles.",
    inconsistent: "We returned your cart to one-time purchase so every item has a consistent purchase option.",
    customer: "Your cart is now a one-time purchase.",
  };
  return messages[reason] ?? messages.customer;
}

function convertedBundleMessage(name) {
  const subject = /^the\b/i.test(name.trim()) ? name : `Your ${name}`;
  return `${subject} was converted into its individual wines for subscription.`;
}

const RECOVERY_KEY_PREFIX = "hob:subscription-cart-recovery:v1:";
const PLAN_INPUT_SELECTOR = "input[data-subscription-plan-input]";

export class CartRecoveryStore {
  constructor(storage) {
    this.storage = storage;
  }

  key(cartToken) {
    return `${RECOVERY_KEY_PREFIX}${cartToken}`;
  }

  save(cartToken, journal) {
    if (!cartToken || !this.storage) {
      throw new Error("We couldn't safely prepare this cart update. Please refresh and try again.");
    }
    this.storage.setItem(this.key(cartToken), JSON.stringify(journal));
  }

  load(cartToken) {
    if (!cartToken || !this.storage) return null;
    const value = this.storage.getItem(this.key(cartToken));
    if (!value) return null;
    const journal = JSON.parse(value);
    const supportedTypes = ["activation", "frequency", "deactivate"];
    if (
      journal?.version !== 1 ||
      journal?.cartToken !== cartToken ||
      !supportedTypes.includes(journal?.type) ||
      !journal?.snapshot?.cart
    ) {
      throw new Error("Saved subscription cart recovery data is invalid.");
    }
    return journal;
  }

  clear(cartToken) {
    if (!cartToken || !this.storage) {
      throw new Error("We couldn't safely finish this cart update. Please retry restoration.");
    }
    this.storage.removeItem(this.key(cartToken));
  }
}

export class SubscriptionCartController {
  constructor({ api, view, config, recoveryStore = null }) {
    this.api = api;
    this.view = view;
    this.config = config;
    this.recoveryStore = recoveryStore;
    this.snapshot = null;
    this.pendingRecovery = null;
    this.currentCart = null;
    this.currentState = null;
    this.reconciliationCart = null;
    this.inFlight = false;
    this.onFinished = null;
  }

  rememberCart(cart) {
    this.currentCart = cart;
    this.currentState = deriveCartState(cart, this.config);
    return this.currentState;
  }

  beginMutation(message, { recovery = false } = {}) {
    if (this.inFlight) throw new Error("A subscription cart update is already in progress.");
    if (this.pendingRecovery && !recovery) throw new Error("Restore the cart before starting another subscription update.");
    this.view.captureFocusIntent?.();
    this.inFlight = true;
    this.view.setBusy(true, message);
    this.view.blockCheckout(true);
    this.view.blockCartEdits?.(true);
    this.view.showRetry(false);
  }

  finishMutation() {
    this.inFlight = false;
    this.view.setBusy(false, "");
    if (this.pendingRecovery) {
      this.view.blockCartEdits?.(true);
      this.view.blockCheckout(true);
      this.view.showRetry(true);
    } else {
      this.view.blockCartEdits?.(false);
      this.view.showRetry(false);
      this.view.blockCheckout(false);
    }
    this.onFinished?.();
  }

  prepareRecovery(cart, details) {
    const journal = {
      version: 1,
      ...details,
      cartToken: cart?.token,
    };
    if (this.recoveryStore) this.recoveryStore.save(cart?.token, journal);
    this.pendingRecovery = journal;
    this.snapshot = journal.snapshot;
    return journal;
  }

  clearRecovery(journal = this.pendingRecovery) {
    if (this.recoveryStore) this.recoveryStore.clear(journal?.cartToken);
    this.pendingRecovery = null;
    this.snapshot = null;
    this.view.showRetry(false);
  }

  async recoverJournal(journal, successMessage = "Your original cart has been restored.") {
    this.pendingRecovery = journal;
    this.snapshot = journal.snapshot ?? null;
    try {
      let restored;
      if (journal.type === "deactivate") {
        restored = await recoverDeactivation(this.api, journal.snapshot);
        if (!verifyOneTime(restored) || !deactivationMatchesSnapshot(restored, journal.snapshot)) {
          throw new Error("Shopify did not restore the intended one-time cart.");
        }
      } else if (journal.type === "frequency") {
        const cart = await this.api.getCart();
        await executeOperations(this.api, buildFrequencyPlan(cart, journal.planId));
        restored = await this.api.getCart();
        if (!verifyPlan(restored, journal.planId)) {
          throw new Error("Shopify did not restore the previous delivery frequency.");
        }
      } else if (journal.type === "activation") {
        restored = await rollbackActivation(this.api, journal.snapshot);
        if (!activationMatchesSnapshot(restored, journal.snapshot)) {
          throw new Error("Shopify did not restore the original cart.");
        }
      } else {
        throw new Error("The saved cart recovery operation is unsupported.");
      }
      this.rememberCart(restored);
      this.reconciliationCart = restored;
      await this.refreshSections();
      this.clearRecovery(journal);
      this.view.announce(successMessage);
      return restored;
    } catch (error) {
      this.pendingRecovery = journal;
      this.snapshot = journal.snapshot ?? null;
      this.view.announce("We couldn't finish restoring your cart. Retry restoration before checkout.");
      this.view.blockCheckout(true);
      this.view.showRetry(true);
      throw error;
    }
  }

  async initialize({ recovery = false, cart: providedCart = null } = {}) {
    this.beginMutation("Checking your cart…", { recovery });
    try {
      const cart = providedCart ?? await this.api.getCart();
      const journal = this.recoveryStore?.load(cart.token);
      if (journal) return await this.recoverJournal(journal);
      const state = this.rememberCart(cart);
      const normalizationSnapshot = { cart, state };
      if (state.subscription.kind === "active" && !state.thresholdMet) {
        return await this.switchToOneTime("below-minimum", normalizationSnapshot, recovery, true);
      }
      if (state.subscription.kind === "inconsistent") {
        return await this.switchToOneTime("inconsistent", normalizationSnapshot, recovery, true);
      }
      this.view.render(state, this.config);
      this.view.clearTransientStatus?.();
      this.pendingRecovery = null;
      return state;
    } catch (error) {
      if (!this.pendingRecovery) {
        this.pendingRecovery = { type: "initialize" };
        this.view.announce("We couldn't verify your cart. Retry before checkout.");
      }
      throw error;
    } finally {
      if (this.inFlight) this.finishMutation();
    }
  }

  async handleCartChanged() {
    const cart = await this.api.getCart();
    const state = this.rememberCart(cart);
    if (state.subscription.kind === "active" && !state.thresholdMet) {
      return this.switchToOneTime("below-minimum");
    }
    if (state.subscription.kind === "inconsistent") {
      return this.switchToOneTime("inconsistent");
    }
    this.view.render(state, this.config);
    return state;
  }

  async refreshSections(providedSections = null) {
    const sections = providedSections ?? await this.api.getSections();
    const required = {
      "cart-items": /data-cart-items(?:\s|=|>)/,
      "cart-footer": /data-cart-footer(?:\s|=|>)/,
      "cart-item-count": /id=["'][^"']*shopify-section[^"']*["']/,
      "cart-live-region": /id=["'][^"']*shopify-section[^"']*["']/,
    };
    for (const [name, target] of Object.entries(required)) {
      const html = sections?.[name];
      let valid = typeof html === "string" && target.test(html);
      if (valid && typeof DOMParser !== "undefined") {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        if (name === "cart-items") valid = Boolean(parsed.querySelector("[data-cart-items]"));
        else if (name === "cart-footer") valid = Boolean(parsed.querySelector("[data-cart-footer]"));
        else valid = Boolean(parsed.body.firstElementChild);
        if (valid && name === "cart-item-count") valid = Number.isFinite(parseInt(parsed.body.textContent.trim(), 10));
      }
      if (!valid) {
        throw new Error(`Shopify returned an invalid ${name} section.`);
      }
    }
    this.view.refresh(sections);
    this.view.restoreFocus();
  }

  async selectPlan(planId) {
    this.beginMutation("Updating your subscription…");
    let state;
    let isActivation;
    let mutationsStarted = false;
    try {
      const cart = await this.api.getCart();
      state = this.rememberCart(cart);
      // Component quantities are totals from the last Liquid render. Never use
      // them to replace a bundle whose current quantity or identity has changed.
      if (state.nativeBundles.some(({ item, config }) => (
        item.key !== config.key ||
        (config.quantity !== undefined && item.quantity !== config.quantity)
      ))) {
        await this.refreshSections();
        this.view.announce("Your cart changed. Please review the updated quantities and choose your purchase option again.");
        return null;
      }
      const ineligible = ineligibleTitlesForPlan(cart, this.config, planId);
      if (!state.thresholdMet) throw new Error("Subscriptions require 12 bottles.");
      if (ineligible.length) throw new Error(`Subscription is unavailable while your cart contains: ${ineligible.join(", ")}.`);
      isActivation = state.subscription.kind !== "active";
      this.view.setBusy(true, state.nativeBundles.length ? "Converting your cart…" : "Updating your subscription…");
      let operations;
      if (isActivation) {
        const transaction = buildActivationTransaction(cart, state, planId);
        operations = transaction.operations;
        this.snapshot = { cart, state, conversionIds: transaction.conversionIds };
      } else {
        operations = buildFrequencyPlan(cart, planId);
        this.snapshot = { cart, state };
      }
      this.prepareRecovery(cart, {
        type: isActivation ? "activation" : "frequency",
        snapshot: this.snapshot,
        ...(isActivation ? {} : { planId: state.subscription.planId }),
      });
      mutationsStarted = true;
      const mutationResponse = await executeOperations(this.api, operations, {
        includeSections: true,
        onProgress: (completed, total) => {
          if (completed < total) this.view.setBusy(true, `Updating your cart… ${completed} of ${total}`);
        },
      });
      const confirmed = cartFromMutationResponse(mutationResponse) ?? await this.api.getCart();
      if (!verifyPlan(confirmed, planId) || !activationContentsMatch(confirmed, this.snapshot)) {
        throw new Error("Shopify returned an inconsistent subscription cart.");
      }
      this.rememberCart(confirmed);
      this.reconciliationCart = confirmed;
      await this.refreshSections(mutationResponse?.sections);
      const bundleNames = state.nativeBundles
        .map(({ item }) => item.product_title)
        .filter(Boolean);
      this.view.announce(
        bundleNames.length
          ? bundleNames
            .map(convertedBundleMessage)
            .join(" ")
          : "Subscription applied. Shipping is included.",
      );
      this.clearRecovery();
      return confirmed;
    } catch (error) {
      if (!mutationsStarted) {
        this.pendingRecovery = null;
        this.snapshot = null;
        throw error;
      }
      if (isActivation) return await this.restoreActivation(error);
      return await this.restoreFrequency(error);
    } finally {
      this.finishMutation();
    }
  }

  async restoreActivation(originalError) {
    await this.recoverJournal(this.pendingRecovery, restoredCartMessage(originalError));
    return null;
  }

  async restoreFrequency(originalError) {
    await this.recoverJournal(this.pendingRecovery, restoredCartMessage(originalError));
    return null;
  }

  async switchToOneTime(reason = "customer", recoverySnapshot = null, recovery = false, owned = false) {
    if (!owned) this.beginMutation("Returning your cart to one-time purchase…", { recovery });
    if (recoverySnapshot) this.snapshot = recoverySnapshot;
    let mutationsStarted = false;
    try {
      const cart = recoverySnapshot?.cart ?? await this.api.getCart();
      const state = this.rememberCart(cart);
      this.snapshot = recoverySnapshot ?? { cart, state };
      this.prepareRecovery(cart, { type: "deactivate", reason, snapshot: this.snapshot });
      mutationsStarted = true;
      const operations = buildDeactivationPlan(cart, state);
      const mutationResponse = await executeOperations(
        this.api,
        operations,
        {
          includeSections: true,
          onProgress: (completed, total) => {
            if (completed < total) this.view.setBusy(true, `Updating your cart… ${completed} of ${total}`);
          },
        },
      );
      const confirmed = cartFromMutationResponse(mutationResponse) ?? await this.api.getCart();
      if (!verifyOneTime(confirmed) || !deactivationMatchesSnapshot(confirmed, this.snapshot)) {
        throw new Error("Shopify did not restore the intended one-time cart.");
      }
      this.rememberCart(confirmed);
      this.reconciliationCart = confirmed;
      await this.refreshSections(mutationResponse?.sections);
      this.view.announce(deactivationMessage(reason));
      this.clearRecovery();
      return confirmed;
    } catch (error) {
      if (!mutationsStarted) {
        this.pendingRecovery = null;
        this.snapshot = null;
        throw error;
      }
      this.view.announce("We couldn't finish restoring your cart. Retry restoration before checkout.");
      this.view.showRetry(true);
      this.view.blockCheckout(true);
      throw error;
    } finally {
      if (this.inFlight) this.finishMutation();
    }
  }

  async retryRestoration() {
    if (!this.pendingRecovery && !this.snapshot) return;
    if (this.pendingRecovery?.type === "initialize") {
      return this.initialize({ recovery: true });
    }
    const journal = this.pendingRecovery ?? {
      version: 1,
      type: "activation",
      snapshot: this.snapshot,
      cartToken: this.snapshot?.cart?.token,
    };
    const isDeactivation = journal.type === "deactivate";
    this.beginMutation(isDeactivation ? "Restoring your one-time cart…" : "Restoring your cart…", { recovery: true });
    try {
      return await this.recoverJournal(
        journal,
        isDeactivation ? deactivationMessage(journal.reason) : "Your original cart has been restored.",
      );
    } finally {
      this.finishMutation();
    }
  }
}

const CART_CONTROL_SELECTOR = [
  "[data-cart-items] input",
  "[data-cart-items] button",
  "[data-cart-items] a[href]",
].join(", ");

const CHECKOUT_CONTROL_SELECTOR = [
  'button[name="checkout"][form="cart"]',
  ".additional-checkout-buttons button",
  ".additional-checkout-buttons input",
  ".additional-checkout-buttons a[href]",
].join(", ");

function includesPlan(planIds = [], planId) {
  return planIds.some((candidate) => String(candidate) === String(planId));
}

function lineSupportsPlan(line, planId) {
  if (line.components?.length) {
    return line.components.every((component) => includesPlan(component.eligiblePlanIds, planId));
  }
  return includesPlan(line.eligiblePlanIds, planId);
}

function planIdFromValue(value, config) {
  const configured = config?.plans?.find((plan) => String(plan.id) === String(value));
  if (configured) return configured.id;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
}

export class SubscriptionCartView {
  constructor(root) {
    this.root = root;
    this.document = root.ownerDocument ?? document;
    this.config = null;
    this.busy = false;
    this.checkoutBlocked = false;
    this.cartEditsBlocked = false;
    this.focusIntent = null;
    this.focusRestorePending = false;
    this.controlState = new WeakMap();
    this.containerState = new WeakMap();
    this.transientStatus = null;
    this.lastAnnouncement = "";
    this.lastThresholdMet = null;
  }

  bind(controller) {
    this.controller = controller;
    this.setFocusKeys();
    const invoke = (action) => {
      try {
        Promise.resolve(action()).catch((error) => {
          if (!controller.pendingRecovery) this.announce(error.message);
        });
      } catch (error) {
        this.announce(error.message);
      }
    };

    this.boundListeners = [];
    const listen = (element, type, handler) => {
      if (!element) return;
      element.addEventListener(type, handler);
      this.boundListeners.push([element, type, handler]);
    };
    this.root.querySelectorAll(PLAN_INPUT_SELECTOR).forEach((input) => {
      listen(input, "change", () => {
        if (input.checked) {
          invoke(() => controller.selectPlan(planIdFromValue(input.value, this.config)));
        }
      });
    });
    const details = this.root.querySelector("[data-subscription-details]");
    const summary = details?.querySelector("summary");
    listen(summary, "click", (event) => {
      if (details.dataset.expandable !== "true") event.preventDefault();
    });
    const oneTime = this.root.querySelector("[data-subscription-one-time]");
    listen(oneTime, "change", () => {
      if (oneTime.checked) invoke(() => controller.switchToOneTime());
    });
    listen(this.root.querySelector("[data-subscription-retry]"), "click", () => {
      invoke(() => controller.retryRestoration());
    });
  }

  destroy() {
    for (const [element, type, handler] of this.boundListeners ?? []) {
      element.removeEventListener(type, handler);
    }
    this.boundListeners = [];
  }

  releaseLocks() {
    this.busy = false;
    this.cartEditsBlocked = false;
    this.checkoutBlocked = false;
    this.root.removeAttribute("aria-busy");
    this.applyControlLocks();
  }

  setFocusKeys() {
    this.root.querySelectorAll(PLAN_INPUT_SELECTOR).forEach((input) => {
      input.dataset.subscriptionFocusKey = `plan:${input.value}`;
    });
    const oneTime = this.root.querySelector("[data-subscription-one-time]");
    if (oneTime) oneTime.dataset.subscriptionFocusKey = "one-time";
    const retry = this.root.querySelector("[data-subscription-retry]");
    if (retry) retry.dataset.subscriptionFocusKey = "retry";
  }

  captureFocusIntent() {
    const active = this.document.activeElement;
    const subscriptionKey = active?.dataset?.subscriptionFocusKey;
    if (subscriptionKey) {
      this.focusIntent = { type: "subscription", key: subscriptionKey };
      this.focusRestorePending = true;
      return;
    }
    const line = active?.closest?.("[data-cart-item-key]");
    if (!line?.dataset?.cartItemKey) {
      this.focusIntent = null;
      this.focusRestorePending = false;
      return;
    }
    const controls = [...line.querySelectorAll("input, button, a[href]")];
    const cartLines = [...this.document.querySelectorAll("[data-cart-item-key]")];
    const controlKey = this.cartControlKey(active);
    const matching = controls.filter((control) => this.cartControlKey(control) === controlKey);
    this.focusIntent = {
      type: "cart",
      lineKey: line.dataset.cartItemKey,
      lineOrdinal: cartLines.indexOf(line),
      controlKey,
      occurrence: Math.max(0, matching.indexOf(active)),
    };
    this.focusRestorePending = true;
  }

  cartControlKey(control) {
    const tagName = control?.tagName?.toLowerCase() ?? "control";
    const role = control?.getAttribute?.("role") ?? (
      tagName === "a" ? "link" : tagName === "button" ? "button" : control?.type || tagName
    );
    const name = control?.getAttribute?.("name") ??
      control?.getAttribute?.("aria-label") ??
      control?.getAttribute?.("id") ??
      control?.type ?? "";
    return `${role}:${name}`;
  }

  render(state, config) {
    this.config = config;
    this.setFocusKeys();
    const minimum = Number(config.minimumBottles) || 12;
    const bottleCount = Math.max(0, Number(state.bottleCount) || 0);
    const remaining = Math.max(0, minimum - bottleCount);
    const progress = this.root.querySelector("[data-subscription-progress-bar]");
    progress.max = minimum;
    progress.value = Math.min(bottleCount, minimum);
    const count = this.root.querySelector("[data-subscription-count]");
    if (count) count.textContent = `${bottleCount} of ${minimum} bottles`;
    const eligiblePlanIds = new Set(
      config.plans
        .filter((plan) => config.lines.every((line) => lineSupportsPlan(line, plan.id)))
        .map((plan) => String(plan.id)),
    );
    this.root.querySelectorAll(PLAN_INPUT_SELECTOR).forEach((input) => {
      const supported = eligiblePlanIds.has(String(input.value));
      input.disabled = !supported || !state.thresholdMet || state.unsupportedTitles.length > 0;
      input.checked = state.subscription.kind === "active" &&
        String(state.subscription.planId) === String(input.value);
      const plan = input.closest("[data-subscription-plan]");
      if (plan) plan.hidden = !supported;
    });

    const maySubscribe = state.thresholdMet &&
      state.unsupportedTitles.length === 0 &&
      eligiblePlanIds.size > 0;
    const thresholdJustReached = this.lastThresholdMet === false && maySubscribe;
    const selectedPlan = config.plans.find((plan) => String(plan.id) === String(state.subscription.planId));
    const selectedPlanCopy = selectedPlan && String(selectedPlan.name).includes("% off")
      ? selectedPlan.name
      : selectedPlan ? `${selectedPlan.name} · ${selectedPlan.discount}% off` : "";
    const remainingBottleLabel = remaining === 1 ? "bottle" : "bottles";
    const progressCopy = state.unsupportedTitles.length
      ? `Subscription is unavailable while your cart contains: ${state.unsupportedTitles.join(", ")}.`
      : !state.thresholdMet
        ? `Add ${remaining} more ${remainingBottleLabel} to unlock shipping included and up to 10% subscription savings.`
        : state.subscription.kind === "active" && selectedPlan
          ? `${selectedPlanCopy} · Shipping included.`
          : maySubscribe
            ? "Shipping included unlocked. Choose a delivery frequency to subscribe and save."
            : "Shipping included unlocked.";
    this.root.querySelector("[data-subscription-progress-copy]").textContent = progressCopy;
    this.root.querySelector("[data-subscription-plans]").hidden = !maySubscribe;
    this.root.querySelector("[data-subscription-bundle-warning]").hidden = state.nativeBundles.length === 0;
    const oneTime = this.root.querySelector("[data-subscription-one-time]");
    if (oneTime) oneTime.checked = state.subscription.kind !== "active";
    const details = this.root.querySelector("[data-subscription-details]");
    if (details) {
      const expandable = maySubscribe || state.nativeBundles.length > 0 || state.subscription.kind === "active";
      details.dataset.expandable = String(expandable);
      if (!expandable) details.open = false;
      else if (thresholdJustReached) details.open = true;
      details.querySelector("summary")?.setAttribute("aria-disabled", String(!expandable));
    }
    this.lastThresholdMet = state.thresholdMet;
    this.root.dataset.state = state.subscription.kind === "active"
      ? "active"
      : maySubscribe ? "eligible" : "locked";
  }

  setBusy(busy, message = "") {
    this.busy = busy;
    if (busy) this.root.setAttribute("aria-busy", "true");
    else this.root.removeAttribute("aria-busy");
    if (message) {
      this.transientStatus = message;
      this.root.querySelector("[data-subscription-status]").textContent = message;
    }
    this.applyControlLocks();
  }

  announce(message) {
    this.transientStatus = null;
    this.lastAnnouncement = message ?? "";
    this.root.querySelector("[data-subscription-status]").textContent = message ?? "";
  }

  clearTransientStatus() {
    const status = this.root.querySelector("[data-subscription-status]");
    if (this.transientStatus && status.textContent === this.transientStatus) status.textContent = "";
    this.transientStatus = null;
  }

  blockCheckout(blocked) {
    this.checkoutBlocked = blocked;
    this.applyControlLocks();
  }

  blockCartEdits(blocked) {
    this.cartEditsBlocked = blocked;
    this.applyControlLocks();
  }

  showRetry(visible) {
    this.root.querySelector("[data-subscription-retry]").hidden = !visible;
  }

  rememberControl(control) {
    if (this.controlState.has(control)) return;
    this.controlState.set(control, {
      disabled: "disabled" in control ? control.disabled : undefined,
      ariaDisabled: control.getAttribute("aria-disabled"),
      tabIndex: control.getAttribute("tabindex"),
    });
  }

  setControlDisabled(control, disabled) {
    if (disabled) {
      this.rememberControl(control);
      if ("disabled" in control) control.disabled = true;
      control.setAttribute("aria-disabled", "true");
      if (!("disabled" in control)) control.setAttribute("tabindex", "-1");
      return;
    }
    const original = this.controlState.get(control);
    if (!original) return;
    if (original.disabled !== undefined) control.disabled = original.disabled;
    if (original.ariaDisabled === null) control.removeAttribute("aria-disabled");
    else control.setAttribute("aria-disabled", original.ariaDisabled);
    if (original.tabIndex === null) control.removeAttribute("tabindex");
    else control.setAttribute("tabindex", original.tabIndex);
    this.controlState.delete(control);
  }

  setContainerDisabled(container, disabled) {
    if (disabled) {
      if (!this.containerState.has(container)) {
        this.containerState.set(container, {
          inert: container.inert,
          ariaDisabled: container.getAttribute("aria-disabled"),
        });
      }
      container.inert = true;
      container.setAttribute("aria-disabled", "true");
      return;
    }
    const original = this.containerState.get(container);
    if (!original) return;
    container.inert = original.inert;
    if (original.ariaDisabled === null) container.removeAttribute("aria-disabled");
    else container.setAttribute("aria-disabled", original.ariaDisabled);
    this.containerState.delete(container);
  }

  applyControlLocks() {
    this.root.querySelectorAll(PLAN_INPUT_SELECTOR).forEach((control) => {
      this.setControlDisabled(control, this.busy || this.cartEditsBlocked);
    });
    const oneTime = this.root.querySelector("[data-subscription-one-time]");
    if (oneTime) this.setControlDisabled(oneTime, this.busy || this.cartEditsBlocked);
    const retry = this.root.querySelector("[data-subscription-retry]");
    if (retry) this.setControlDisabled(retry, this.busy);
    const cartDisabled = this.busy || this.cartEditsBlocked;
    this.document.querySelectorAll(CART_CONTROL_SELECTOR).forEach((control) => {
      this.setControlDisabled(control, cartDisabled);
    });
    this.document.querySelectorAll("[data-cart-items]").forEach((container) => {
      this.setContainerDisabled(container, cartDisabled);
    });
    const checkoutDisabled = this.busy || this.checkoutBlocked;
    this.document.querySelectorAll(CHECKOUT_CONTROL_SELECTOR).forEach((control) => {
      this.setControlDisabled(control, checkoutDisabled);
    });
    this.document.querySelectorAll(".additional-checkout-buttons").forEach((container) => {
      this.setContainerDisabled(container, checkoutDisabled);
    });
    if (this.focusRestorePending && !this.busy && !this.cartEditsBlocked) {
      this.focusRestorePending = false;
      queueMicrotask(() => this.focusEquivalent());
    }
  }

  refresh(sections) {
    if (!this.focusIntent) this.captureFocusIntent();
    const widgetRoots = [...this.document.querySelectorAll("[data-subscription-cart]")];
    const widgetIndex = widgetRoots.indexOf(this.root);
    window.dispatchEvent(new CustomEvent("cascade:modalcart:cartqtychange", {
      detail: { response: { sections } },
    }));
    if (widgetIndex >= 0) {
      const replacement = this.document.querySelectorAll("[data-subscription-cart]")[widgetIndex];
      if (replacement) {
        this.root = replacement;
        this.setFocusKeys();
      }
    }
    this.applyControlLocks();
  }

  restoreFocus() {
    if (!this.focusIntent && this.focusKey) {
      this.focusIntent = { type: "subscription", key: this.focusKey };
    }
    if (!this.focusIntent) return;
    if (this.busy || this.cartEditsBlocked) {
      this.focusRestorePending = true;
      return;
    }
    if (!this.focusEquivalent()) queueMicrotask(() => this.focusEquivalent());
  }

  focusEquivalent() {
    const intent = this.focusIntent;
    if (!intent) return true;
    if (intent.type === "subscription") {
      const equivalent = [...this.root.querySelectorAll("[data-subscription-focus-key]")]
        .find((control) => control.dataset.subscriptionFocusKey === intent.key);
      if (equivalent && !equivalent.disabled && !equivalent.hidden) {
        const collapsedDetails = equivalent.closest("details:not([open])");
        if (collapsedDetails) {
          const summary = collapsedDetails.querySelector("summary");
          if (summary) {
            summary.focus();
            this.focusIntent = null;
            this.focusKey = null;
            return true;
          }
        }
        equivalent.focus();
        this.focusIntent = null;
        this.focusKey = null;
        return true;
      }
    } else if (intent.type === "cart") {
      const cartLines = [...this.document.querySelectorAll("[data-cart-item-key]")];
      const line = cartLines.find((candidate) => candidate.dataset.cartItemKey === intent.lineKey) ??
        (intent.lineOrdinal >= 0 ? cartLines[intent.lineOrdinal] : null);
      const matches = line
        ? [...line.querySelectorAll("input, button, a[href]")]
          .filter((control) => this.cartControlKey(control) === intent.controlKey)
        : [];
      const equivalent = matches[intent.occurrence] ?? matches[0] ?? null;
      if (equivalent && !equivalent.disabled && !equivalent.hidden) {
        equivalent.focus();
        this.focusIntent = null;
        return true;
      }
    }
    const fallback = this.root.querySelector("[data-subscription-heading]") ??
      this.document.querySelector?.("[data-cart-items] input, [data-cart-items] button");
    if (!fallback) return false;
    fallback.setAttribute("tabindex", "-1");
    fallback.focus();
    this.focusIntent = null;
    this.focusKey = null;
    return true;
  }
}

function scheduleReconciliation(element, owner) {
  if (!owner.reconcilePending || owner.reconcileScheduled) return;
  owner.reconcileScheduled = true;
  queueMicrotask(() => {
    owner.reconcileScheduled = false;
    if (element.__subscriptionCartOwner !== owner || owner.controller.inFlight) return;
    owner.reconcilePending = false;
    owner.announcementCarryPending = true;
    delete element.dataset.subscriptionCartReady;
    initializeSubscriptionCarts(owner.view.document);
  });
}

function browserRecoveryStore() {
  try {
    return new CartRecoveryStore(window.localStorage);
  } catch {
    return new CartRecoveryStore(null);
  }
}

let missingSellingPlanWarningShown = false;

export function initializeSubscriptionCarts(root = document) {
  root.querySelectorAll("[data-subscription-cart]").forEach((element) => {
    const owner = element.__subscriptionCartOwner;
    if (element.dataset.subscriptionCartReady && owner) return;
    if (owner?.controller.inFlight) {
      element.dataset.subscriptionCartReady = "true";
      owner.reconcilePending = true;
      return;
    }
    const transferredRecovery = owner?.controller.pendingRecovery;
    const transferredSnapshot = owner?.controller.snapshot;
    const transferredCart = owner?.announcementCarryPending
      ? owner.controller.reconciliationCart
      : null;
    const transferredThresholdMet = owner?.view.lastThresholdMet ?? null;
    const transferredAnnouncement = transferredRecovery || owner?.announcementCarryPending
      ? owner?.view.lastAnnouncement
      : "";
    if (transferredRecovery) owner.view.releaseLocks();
    owner?.view.destroy();
    element.dataset.subscriptionCartReady = "true";
    const config = JSON.parse(
      element.querySelector("[data-subscription-cart-config]").textContent,
    );
    if (!config.plans?.length && !missingSellingPlanWarningShown) {
      console.warn("[House of Brown subscriptions] No 'Subscribe and save' selling plans were found. Subscription choices are hidden.");
      missingSellingPlanWarningShown = true;
    }
    const api = new CartApi(config.routes);
    const view = new SubscriptionCartView(element);
    view.lastThresholdMet = transferredThresholdMet;
    const controller = new SubscriptionCartController({ api, view, config, recoveryStore: browserRecoveryStore() });
    const nextOwner = {
      view,
      controller,
      reconcilePending: false,
      reconcileScheduled: false,
      announcementCarryPending: false,
    };
    element.__subscriptionCartOwner = nextOwner;
    controller.onFinished = () => scheduleReconciliation(element, nextOwner);
    view.bind(controller);
    if (transferredRecovery) {
      controller.pendingRecovery = transferredRecovery;
      controller.snapshot = transferredSnapshot;
      view.config = config;
      view.blockCartEdits(true);
      view.blockCheckout(true);
      view.showRetry(true);
      if (transferredAnnouncement) view.announce(transferredAnnouncement);
      return;
    }
    controller.initialize({ cart: transferredCart })
      .then(() => {
        if (transferredAnnouncement) view.announce(transferredAnnouncement);
      })
      .catch((error) => {
        if (!controller.pendingRecovery) view.announce(error.message);
      });
  });
}

export function expandEligibleSubscriptionDrawers(root = document) {
  root.querySelectorAll(".subscription-cart--drawer").forEach((widget) => {
    if (widget.dataset.state !== "eligible" && widget.dataset.state !== "active") return;
    const details = widget.querySelector("[data-subscription-details]");
    if (details?.dataset.expandable === "true") details.open = true;
  });
}

if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initializeSubscriptionCarts());
  const initializeAfterCascadeMorph = () => queueMicrotask(() => initializeSubscriptionCarts());
  window.addEventListener("cascade:modalcart:afteradditem", initializeAfterCascadeMorph);
  window.addEventListener("cascade:modalcart:cartqtychange", initializeAfterCascadeMorph);
  document.body.addEventListener("right-drawer-is-open", () => expandEligibleSubscriptionDrawers());
}
